import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Link,
  Card,
  CardContent,
  Alert,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import Logo from './Logo';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    branch: '',
    vehicleNumber: '',
    isAdmin: false,
    adminCode: '',
    licensePlate: '',
    vin: '',
    make: '',
    model: '',
    year: ''
  });
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [emailExists, setEmailExists] = useState(false);
  const [showAccountExistsDialog, setShowAccountExistsDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branchesData = await apiService.getBranches();
        setBranches(branchesData);
      } catch (error) {
        setError('Failed to fetch required data. Please try again or contact support.');
      }
    };
    fetchBranches();
  }, []);

  const validateForm = () => {
    if (!formData.firstName || formData.firstName.trim().length < 2) {
      setError('First name must be at least 2 characters long');
      return false;
    }

    if (!formData.lastName || formData.lastName.trim().length < 2) {
      setError('Last name must be at least 2 characters long');
      return false;
    }

    if (!formData.email) {
      setError('Email is required');
      return false;
    }

    // Internal email validation without revealing domain requirement
    if (!formData.email.toLowerCase().endsWith('@mascottec.com')) {
      setError('Invalid email address');
      return false;
    }

    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!formData.branch) {
      setError('Please select a branch');
      return false;
    }

    if (!formData.isAdmin && !formData.vehicleNumber) {
      setError('Unit number is required for non-admin users');
      return false;
    }

    if (!formData.isAdmin) {
      // Reject 2-character unit numbers client-side
      if (formData.vehicleNumber && String(formData.vehicleNumber).length === 2) {
        setError('Invalid unit number. Please enter the unit number from your vehicle or maintenance card.');
        return false;
      }
      if (!formData.licensePlate || !formData.licensePlate.trim()) {
        setError('License plate is required');
        return false;
      }
      if (!formData.vin || formData.vin.trim().length < 11) {
        setError('VIN must be at least 11 characters');
        return false;
      }
      if (!formData.make.trim()) {
        setError('Make is required');
        return false;
      }
      if (!formData.model.trim()) {
        setError('Model is required');
        return false;
      }
      if (!formData.year || String(formData.year).length !== 4) {
        setError('Year must be a 4-digit number');
        return false;
      }
    }

    if (formData.isAdmin && !formData.adminCode) {
      setError('Admin code is required for admin registration');
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Force ALL CAPS in real time for applicable fields, and sanitize username (no punctuation)
    const upperFields = new Set([
      'firstName', 'lastName',
      'vehicleNumber', 'licensePlate', 'vin', 'make', 'model'
    ]);
    const sanitizeUsername = (str) => String(str || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const nextValue = type === 'checkbox' ? checked : (
      name === 'username' ? sanitizeUsername(value) : (
        upperFields.has(name) ? String(value).toUpperCase() : value
      )
    );
    setFormData(prevState => ({
      ...prevState,
      [name]: nextValue
    }));
    setError('');
    if (name === 'email') {
      setEmailExists(false);
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Generate username from first and last name, sanitized (letters and spaces only), UPPERCASE
      const submissionData = {
        ...formData,
        username: `${String(formData.firstName || '').replace(/[^A-Za-z\s]/g, '').trim()} ${String(formData.lastName || '').replace(/[^A-Za-z\s]/g, '').trim()}`.toUpperCase()
      };

      // Register the user
      const response = await apiService.register(submissionData);
      
      // Redirect to verification page with email and any conflict warning
      navigate('/verify-email', { 
        state: { 
          email: formData.email,
          username: submissionData.username,
          vehicleConflict: response?.vehicleConflict || false,
          conflictMessage: response?.conflictMessage || null
        }
      });
    } catch (error) {
      console.error('Registration error:', error);
      // Handle specific error types from the backend
      if (typeof error === 'string') {
        setError(error);
      } else if (error?.response?.data?.errorType === 'ACCOUNT_EXISTS') {
        setError(error.response.data.message);
        setEmailExists(true);
        setShowAccountExistsDialog(true);
      } else if (error?.response?.data?.errorType === 'PENDING_REGISTRATION') {
        setError(error.response.data.message);
      } else if (error?.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Registration failed. Please try again or contact support.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Logo height="80px" />
            </Box>
            
            <Typography component="h1" variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
              Register a new account
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isAdmin}
                    onChange={handleChange}
                    name="isAdmin"
                  />
                }
                label="Register as Admin"
                sx={{ mb: 2 }}
              />

              {formData.isAdmin && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="adminCode"
                  label="Admin Code"
                  type="password"
                  id="adminCode"
                  value={formData.adminCode}
                  onChange={handleChange}
                />
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={error.includes('First name')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={error.includes('Last name')}
                  />
                </Grid>
              </Grid>

              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                error={emailExists || error.includes('email')}
                helperText={emailExists ? 'An account with this email already exists. Use Forgot Password to reset your password.' : undefined}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                error={error.includes('Password')}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={error.includes('Passwords do not match')}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                select
                name="branch"
                label="Branch"
                value={formData.branch}
                onChange={handleChange}
                error={error.includes('branch')}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.name}>
                    {branch.name}
                  </MenuItem>
                ))}
              </TextField>

              {!formData.isAdmin && (
                <>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="vehicleNumber"
                    label="Unit Number"
                    type="text"
                    id="vehicleNumber"
                    value={formData.vehicleNumber}
                     onChange={handleChange}
                     onBlur={handleBlur}
                     helperText={touched.vehicleNumber && formData.vehicleNumber && formData.vehicleNumber.length === 2 ? '“xx” is not a valid unit number. Enter the unit number from your vehicle or maintenance card.' : ''}
                     error={Boolean(touched.vehicleNumber && formData.vehicleNumber && formData.vehicleNumber.length === 2)}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="licensePlate"
                    label="License Plate"
                    type="text"
                    value={formData.licensePlate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="vin"
                    label="VIN"
                    type="text"
                    value={formData.vin}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="make"
                        label="Make"
                        type="text"
                        value={formData.make}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="model"
                        label="Model"
                        type="text"
                        value={formData.model}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="year"
                        label="Year"
                        type="number"
                        value={formData.year}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Register'
                )}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Already have an account? Login
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Account exists dialog */}
      <Dialog open={showAccountExistsDialog} onClose={() => setShowAccountExistsDialog(false)}>
        <DialogTitle>Account already exists</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            An account with this email already exists. You can reset your password using the Forgot Password option.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAccountExistsDialog(false)}>Use different email</Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowAccountExistsDialog(false);
              navigate(`/forgot-password?email=${encodeURIComponent(formData.email)}`);
            }}
          >
            Go to Forgot Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RegistrationForm;